import React, { useState, useEffect } from 'react';
import { AlertTriangle, Clock, CreditCard, ShieldAlert, Loader2, ArrowRight } from 'lucide-react';
import type { PaymentInfo } from '../../utils/paymentUtils';
import { DEFAULT_PAYMENT_DEADLINE, DEFAULT_PAYMENT_DEADLINE_DISPLAY } from '../../utils/paymentUtils';

interface PaymentWarningBannerProps {
    paymentInfo: PaymentInfo;
}

export const PaymentWarningBanner: React.FC<PaymentWarningBannerProps> = ({ paymentInfo }) => {
    const [isProcessing] = useState(false);
    const [timeState, setTimeState] = useState(() => {
        const now = new Date();
        const deadline = paymentInfo?.deadlineDate ? new Date(paymentInfo.deadlineDate) : DEFAULT_PAYMENT_DEADLINE;
        const diffMs = deadline.getTime() - now.getTime();
        const totalSecs = Math.max(0, Math.floor(diffMs / 1000));
        return {
            diffMs,
            isExpired: diffMs <= 0,
            days: Math.floor(totalSecs / 86400),
            hours: Math.floor((totalSecs % 86400) / 3600),
            minutes: Math.floor((totalSecs % 3600) / 60),
            seconds: totalSecs % 60,
        };
    });

    // Real-time 1-second countdown ticker
    useEffect(() => {
        if (paymentInfo.isFullyPaid || paymentInfo.bannerLevel === 'none') {
            return;
        }

        const timer = setInterval(() => {
            const now = new Date();
            const deadline = paymentInfo?.deadlineDate ? new Date(paymentInfo.deadlineDate) : DEFAULT_PAYMENT_DEADLINE;
            const diffMs = deadline.getTime() - now.getTime();
            const totalSecs = Math.max(0, Math.floor(diffMs / 1000));

            setTimeState({
                diffMs,
                isExpired: diffMs <= 0,
                days: Math.floor(totalSecs / 86400),
                hours: Math.floor((totalSecs % 86400) / 3600),
                minutes: Math.floor((totalSecs % 3600) / 60),
                seconds: totalSecs % 60,
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [paymentInfo]);

    if (paymentInfo.isFullyPaid || paymentInfo.bannerLevel === 'none') {
        return null;
    }

    const handlePayBalance = async () => {
        const directSquareLink = paymentInfo.paymentLink || 'https://square.link/u/HjJ7Mn7U';
        window.open(directSquareLink, '_blank');
    };

    const isExpired = timeState.isExpired || paymentInfo.bannerLevel === 'expired';
    const totalHoursLeft = timeState.diffMs / (1000 * 60 * 60);
    const isRed = !isExpired && (totalHoursLeft <= 48 || paymentInfo.bannerLevel === 'red');
    const isOrange = !isExpired && !isRed && (totalHoursLeft <= 96 || paymentInfo.bannerLevel === 'orange');

    const deadlineText = paymentInfo.deadlineDisplay || DEFAULT_PAYMENT_DEADLINE_DISPLAY;

    const bgClass = isExpired
        ? 'bg-red-600 text-white border-red-700 shadow-red-950/20'
        : isRed
        ? 'bg-red-500/10 border-red-500/40 text-red-900 dark:text-red-300 shadow-red-500/5'
        : isOrange
        ? 'bg-amber-500/10 border-amber-500/40 text-amber-900 dark:text-amber-200 shadow-amber-500/5'
        : 'bg-yellow-500/10 border-yellow-500/40 text-yellow-950 dark:text-yellow-200 shadow-yellow-500/5';

    const iconColor = isExpired ? 'text-white' : isRed ? 'text-red-500' : isOrange ? 'text-amber-500' : 'text-yellow-600';
    const countdownPillBg = isExpired
        ? 'bg-white/20 text-white border-white/30'
        : isRed
        ? 'bg-red-500/20 text-red-700 dark:text-red-300 border-red-500/30'
        : isOrange
        ? 'bg-amber-500/20 text-amber-800 dark:text-amber-200 border-amber-500/30'
        : 'bg-yellow-500/20 text-yellow-900 dark:text-yellow-200 border-yellow-500/30';

    return (
        <div className={`p-4 sm:p-5 rounded-2xl border-2 shadow-lg flex flex-col xl:flex-row items-stretch xl:items-center justify-between gap-5 transition-all animate-in slide-in-from-top duration-300 ${bgClass}`}>
            {/* Left Column: Details & Warning Message */}
            <div className="flex items-start sm:items-center gap-3.5 flex-1 min-w-0">
                <div className={`p-3 rounded-xl shrink-0 mt-0.5 sm:mt-0 ${isExpired ? 'bg-white/20' : 'bg-white/80 dark:bg-black/30 border border-black/5 dark:border-white/10'}`}>
                    {isExpired ? (
                        <ShieldAlert size={24} className={iconColor} />
                    ) : isRed ? (
                        <AlertTriangle size={24} className={iconColor} />
                    ) : (
                        <Clock size={24} className={iconColor} />
                    )}
                </div>

                <div className="space-y-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-black text-xs uppercase tracking-widest flex items-center gap-1.5">
                            {isExpired
                                ? 'Access Suspended - Payment Overdue'
                                : isRed
                                ? 'Critical Payment Notice (< 48h)'
                                : isOrange
                                ? 'Payment Deadline Approaching'
                                : 'Tuition Balance Access Notice'}
                        </span>
                        <span className="text-[10px] font-extrabold px-2.5 py-0.5 rounded-full bg-white/40 dark:bg-black/30 uppercase tracking-wider border border-current/20">
                            50% Balance Remaining: ${paymentInfo.remainingBalance}
                        </span>
                    </div>

                    <p className="text-xs font-semibold leading-relaxed opacity-95">
                        {isExpired ? (
                            <span>
                                Your access to lessons, videos, and platform materials has been revoked because the deadline (<span className="underline font-black">{deadlineText}</span>) passed. Pay your remaining balance of <strong className="font-black">${paymentInfo.remainingBalance}</strong> to instantly restore access.
                            </span>
                        ) : (
                            <span>
                                Access revocation deadline: <strong className="font-black underline">{deadlineText}</strong>. Platform access will be suspended when the countdown timer below reaches zero.
                            </span>
                        )}
                    </p>
                </div>
            </div>

            {/* Right Column: Live Countdown Badges & Pay Button */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 shrink-0">
                {!isExpired && (
                    <div className="flex items-center justify-center gap-1 sm:gap-1.5 px-3 py-2 rounded-xl bg-white/40 dark:bg-black/30 border border-current/15">
                        {/* Days */}
                        <div className={`px-2 py-1 rounded-lg text-center font-mono border ${countdownPillBg}`}>
                            <div className="text-sm sm:text-base font-black leading-none">{String(timeState.days).padStart(2, '0')}</div>
                            <div className="text-[8px] font-black uppercase tracking-wider opacity-80">Days</div>
                        </div>
                        <span className="font-black text-xs opacity-60">:</span>
                        {/* Hours */}
                        <div className={`px-2 py-1 rounded-lg text-center font-mono border ${countdownPillBg}`}>
                            <div className="text-sm sm:text-base font-black leading-none">{String(timeState.hours).padStart(2, '0')}</div>
                            <div className="text-[8px] font-black uppercase tracking-wider opacity-80">Hours</div>
                        </div>
                        <span className="font-black text-xs opacity-60">:</span>
                        {/* Minutes */}
                        <div className={`px-2 py-1 rounded-lg text-center font-mono border ${countdownPillBg}`}>
                            <div className="text-sm sm:text-base font-black leading-none">{String(timeState.minutes).padStart(2, '0')}</div>
                            <div className="text-[8px] font-black uppercase tracking-wider opacity-80">Mins</div>
                        </div>
                        <span className="font-black text-xs opacity-60">:</span>
                        {/* Seconds */}
                        <div className={`px-2 py-1 rounded-lg text-center font-mono border ${countdownPillBg}`}>
                            <div className="text-sm sm:text-base font-black leading-none text-brand-emerald dark:text-emerald-400 animate-pulse">{String(timeState.seconds).padStart(2, '0')}</div>
                            <div className="text-[8px] font-black uppercase tracking-wider opacity-80">Secs</div>
                        </div>
                    </div>
                )}

                <button
                    type="button"
                    onClick={handlePayBalance}
                    disabled={isProcessing}
                    className={`px-6 h-12 rounded-xl text-xs font-black uppercase tracking-widest shadow-lg transition-all border-none cursor-pointer flex items-center justify-center gap-2 hover:scale-105 active:scale-95 disabled:opacity-50 shrink-0 ${
                        isExpired
                            ? 'bg-white text-red-600 hover:bg-slate-100 shadow-black/20'
                            : 'bg-brand-emerald text-white hover:bg-emerald-600 shadow-brand-emerald/20'
                    }`}
                >
                    {isProcessing ? (
                        <><Loader2 size={16} className="animate-spin" /> Processing Square...</>
                    ) : (
                        <><CreditCard size={16} /> Pay ${paymentInfo.remainingBalance} & {isExpired ? 'Restore Access' : 'Settle Balance'} <ArrowRight size={14} /></>
                    )}
                </button>
            </div>
        </div>
    );
};
